// T-373 — Selettore foto del piatto per il Menu Manager.
//
// Flusso: scatta/scegli → auto-pad su 16:10 (sfondo = colore `paper` del tema)
// → anteprima. "Ritocca" opzionale = pan/zoom con cornice 16:10 (per foto su
// sfondo non neutro) che produce un ritaglio `cover`. Emette un JPEG Blob
// pronto per l'upload; l'upload effettivo (che richiede slug + id piatto) lo
// fa il DishEditorModal.

import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonRange,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { cameraOutline, imageOutline, trashOutline, cropOutline } from 'ionicons/icons';
import { useCallback, useRef, useState } from 'react';
import { getCaptureProvider } from '../../../lib/adapters/camera/index.js';
import { padToRatio } from '../../../lib/imageCompress.js';
import { toUserMessage } from '../../../lib/errors.js';

const RATIO = 16 / 10;

interface Props {
  currentUrl: string | null;
  paperColor: string;
  onStaged: (blob: Blob, previewUrl: string) => void;
  onRemove: () => void;
  onError?: (msg: string) => void;
}

export function PhotoPicker({ currentUrl, paperColor, onStaged, onRemove, onError }: Props): JSX.Element {
  const [preview, setPreview] = useState<string | null>(null);
  // Ultimo scatto grezzo, per il "Ritocca".
  const [rawB64, setRawB64] = useState<string | null>(null);
  const [rawMime, setRawMime] = useState<string>('image/jpeg');
  const [reframeOpen, setReframeOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const shownUrl = preview ?? currentUrl;

  const stageBlob = useCallback(
    (blob: Blob) => {
      const url = URL.createObjectURL(blob);
      setPreview(url);
      onStaged(blob, url);
    },
    [onStaged],
  );

  const capture = useCallback(
    async (source: 'camera' | 'gallery') => {
      setBusy(true);
      try {
        const photo = await getCaptureProvider().takePhoto(source);
        if (!photo) return;
        setRawB64(photo.base64);
        setRawMime(photo.mime);
        const blob = await padToRatio(photo.base64, photo.mime, { ratio: RATIO, bg: paperColor });
        stageBlob(blob);
      } catch (err) {
        onError?.(toUserMessage(err, 'Cattura annullata'));
      } finally {
        setBusy(false);
      }
    },
    [paperColor, stageBlob, onError],
  );

  return (
    <div className="menu-photo-picker">
      {shownUrl ? (
        <div
          className="menu-photo-preview"
          style={{
            aspectRatio: '16 / 10',
            backgroundImage: `url('${shownUrl}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            borderRadius: 12,
            border: '1px solid var(--ion-color-step-150, #e0e0e0)',
          }}
          aria-label="Anteprima foto piatto"
        />
      ) : (
        <div
          style={{
            aspectRatio: '16 / 10',
            borderRadius: 12,
            border: '1px dashed var(--ion-color-step-300, #c0c0c0)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--ion-color-medium)',
            fontSize: 13,
          }}
        >
          Nessuna foto
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <IonButton size="small" fill="outline" disabled={busy} onClick={() => void capture('gallery')}>
          <IonIcon slot="start" icon={imageOutline} />
          {shownUrl ? 'Sostituisci' : 'Galleria'}
        </IonButton>
        <IonButton size="small" fill="outline" disabled={busy} onClick={() => void capture('camera')}>
          <IonIcon slot="start" icon={cameraOutline} />
          Scatta
        </IonButton>
        {rawB64 && (
          <IonButton size="small" fill="clear" disabled={busy} onClick={() => setReframeOpen(true)}>
            <IonIcon slot="start" icon={cropOutline} />
            Ritocca
          </IonButton>
        )}
        {shownUrl && (
          <IonButton
            size="small"
            fill="clear"
            color="danger"
            disabled={busy}
            onClick={() => {
              setPreview(null);
              setRawB64(null);
              onRemove();
            }}
          >
            <IonIcon slot="start" icon={trashOutline} />
            Rimuovi
          </IonButton>
        )}
      </div>

      {rawB64 && (
        <ReframeModal
          isOpen={reframeOpen}
          b64={rawB64}
          mime={rawMime}
          bg={paperColor}
          onClose={() => setReframeOpen(false)}
          onConfirm={(blob) => {
            stageBlob(blob);
            setReframeOpen(false);
          }}
          onError={onError}
        />
      )}
    </div>
  );
}

// ── Reframe (pan/zoom) ────────────────────────────────────────────────────────
function ReframeModal(props: {
  isOpen: boolean;
  b64: string;
  mime: string;
  bg: string;
  onClose: () => void;
  onConfirm: (blob: Blob) => void;
  onError?: (msg: string) => void;
}): JSX.Element {
  const { isOpen, b64, mime, bg, onClose, onConfirm, onError } = props;
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const natural = useRef<{ w: number; h: number } | null>(null);
  const viewport = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  const [busy, setBusy] = useState(false);

  const onImgLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    natural.current = { w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight };
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    drag.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y };
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    setPan({ x: drag.current.px + (e.clientX - drag.current.x), y: drag.current.py + (e.clientY - drag.current.y) });
  };
  const onPointerUp = () => {
    drag.current = null;
  };

  const confirm = useCallback(async () => {
    const vp = viewport.current;
    const nat = natural.current;
    if (!vp || !nat) {
      onClose();
      return;
    }
    setBusy(true);
    try {
      const Vw = vp.clientWidth;
      const Vh = vp.clientHeight;
      const coverScale = Math.max(Vw / nat.w, Vh / nat.h);
      const dispScale = coverScale * zoom;
      // top-left dell'immagine (px viewport): centrata + pan.
      const dispW = nat.w * dispScale;
      const dispH = nat.h * dispScale;
      const ox = (Vw - dispW) / 2 + pan.x;
      const oy = (Vh - dispH) / 2 + pan.y;
      // regione sorgente visibile nella viewport.
      let sx = -ox / dispScale;
      let sy = -oy / dispScale;
      const sw = Vw / dispScale;
      const sh = Vh / dispScale;
      sx = Math.max(0, Math.min(sx, nat.w - sw));
      sy = Math.max(0, Math.min(sy, nat.h - sh));
      const blob = await padToRatio(b64, mime, { ratio: 16 / 10, bg, crop: { sx, sy, sw, sh } });
      onConfirm(blob);
    } catch (err) {
      onError?.(toUserMessage(err, 'Ritaglio fallito'));
    } finally {
      setBusy(false);
    }
  }, [zoom, pan, b64, mime, bg, onConfirm, onClose, onError]);

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Ritocca foto</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>Annulla</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <p style={{ fontSize: 13, color: 'var(--ion-color-medium)', marginTop: 0 }}>
          Trascina per spostare, usa lo slider per lo zoom. Ciò che vedi nella cornice sarà la foto.
        </p>
        <div
          ref={viewport}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          style={{
            position: 'relative',
            width: '100%',
            aspectRatio: '16 / 10',
            overflow: 'hidden',
            borderRadius: 12,
            background: bg,
            touchAction: 'none',
            cursor: 'grab',
          }}
        >
          <img
            src={`data:${mime};base64,${b64}`}
            onLoad={onImgLoad}
            alt=""
            draggable={false}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: '100%',
              height: '100%',
              // Baseline `cover` = coverScale usato nel calcolo del crop; scale(zoom)
              // e translate(pan) replicano esattamente la matematica di `confirm()`.
              objectFit: 'cover',
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: 'center',
            }}
          />
        </div>
        <IonRange
          min={1}
          max={3}
          step={0.01}
          value={zoom}
          onIonInput={(e) => setZoom(e.detail.value as number)}
          style={{ marginTop: 12 }}
        >
          <IonIcon slot="start" icon={cropOutline} size="small" />
        </IonRange>
        <IonButton expand="block" disabled={busy} onClick={() => void confirm()}>
          Usa questo ritaglio
        </IonButton>
      </IonContent>
    </IonModal>
  );
}
